import { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { useAuth } from "../contexts/authContext";
import { hasPermission } from "../lib/permissions";
import { permissionDeniedTooltip } from "../pages/admin/roles/rolePermissionGroups";
import { PermissionId } from "../models/permission";

interface PermissionGateProps {
  permission: PermissionId;
  children: ReactNode;
}

// Keeps a gated affordance visible but inert when the current workspace member
// lacks the permission. Wraps children in a tooltip that explains the
// role-based denial; uses CSS to disable interactivity on the children
// (pointer-events-none + opacity) so it works for both <Button> and complex
// feature components that don't accept a `disabled` prop. The tooltip itself
// is mounted on the outer span so hover detection still works.
export function PermissionGate({ permission, children }: PermissionGateProps) {
  const { workspaceMember } = useAuth();
  if (hasPermission(workspaceMember, permission)) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-disabled
          className="inline-block cursor-not-allowed [&>*]:pointer-events-none [&>*]:opacity-50"
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{permissionDeniedTooltip(permission)}</TooltipContent>
    </Tooltip>
  );
}
